import React, { useCallback, useEffect, useRef } from 'react';
import { flowRight, get } from 'lodash';
import type { ConnectedProps } from 'react-redux';
import type { WithTranslation } from '@wix/yoshi-flow-editor';
import {
  SECTION_CATEGORY,
  createPageUrl,
  getTagBySlug,
  isLayoutProGallery,
  type LayoutType,
} from '@wix/communities-blog-client-common';
import stylesParams from '@app/components/Blog/stylesParams';
import type { PostAction } from '@app/external/common/constants/post-actions';
import { PostTitleTagProvider } from '@app/external/common/context/post-title-tag-context';
import AnimatedLoader from '../../../common/components/animated-loader';
import withComponents, {
  type WithComponents,
} from '../../../common/components/components-provider/with-components';
import { EmptyLayout } from '../../../common/components/layout';
import Page from '../../../common/components/page';
import { connect } from '../../../common/components/runtime-context';
import Pagination from '../../../common/containers/pagination';
import withAuth, { type WithAuth } from '../../../common/hoc/with-auth';
import withLayoutProps, { type WithLayoutProps } from '../../../common/hoc/with-layout-props';
import withPermissions, { type WithPermissions } from '../../../common/hoc/with-permissions';
import withTranslate from '../../../common/hoc/with-translate';
import { getCanSeeMoreButton } from '../../../common/selectors/app-settings-selectors';
import {
  isExcludePostContentSupported,
  isPostContentRequired,
} from '../../../common/selectors/layout-selectors';
import {
  getPostsByTagIdAndPage,
  getSortedPostsByTagId,
} from '../../../common/selectors/post-selectors';
import { getTagUrl } from '../../../common/services/get-tag-url';
import { handleNavigation } from '../../../common/services/navigation';
import { isSite as isSiteSelector } from '../../../common/store/basic-params/basic-params-selectors';
import { getIsTagLoaded } from '../../../common/store/is-loaded/is-loaded-selectors';
import { getIsTagLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { getSectionUrl } from '../../../common/store/topology/topology-selectors';
import type { NormalizedPost } from '../../../common/types';
import PostList from '../../components/post-list';
import type { RouteCategoryParams } from '../../constants/routes';
import withPaginationSettings, {
  type WithPaginationSettings,
} from '../../hoc/with-pagination-settings';

const Layout = EmptyLayout;

type OwnProps = {
  params: RouteCategoryParams;
  fetchTagPosts: () => void;
  setIsLoaded: (loaded: boolean) => void;
  page: number;
  layoutType: LayoutType;
  pageSize?: number;
  entityCount?: number;
  showPagination?: boolean;
  canSee: (action: PostAction, type: 'post', post: NormalizedPost) => boolean;
};

type Props = OwnProps &
  WithComponents &
  WithPermissions &
  WithPaginationSettings &
  WithLayoutProps &
  WithAuth &
  WithTranslation &
  ConnectedProps<typeof connector>;

const TagsPage: React.FC<Props> = (props) => {
  const {
    tagId,
    currentPagePosts,
    allPosts,
    entityCount,
    isTagLoaded,
    isTagLoading,
    layoutType,
    showPagination,
    PostListProGallery,
    canSeeMoreButton,
    page,
    pageSize,
    isSite,
    tagPath,
    tagUrl,
  } = props;
  // Read by the stable loadMore/loadRest and the unmount cleanup, so must be the latest render's
  const latest = useRef(props);
  latest.current = props;
  const prev = useRef<Props>();

  const fetchPosts = (tagId?: string, page = latest.current.page) => {
    if (latest.current.isBlocked || !tagId) {
      return Promise.resolve();
    }

    return latest.current.fetchTagPosts({
      tagId,
      page,
    });
  };

  useEffect(() => {
    const prevProps = prev.current;
    prev.current = props;
    if (!prevProps) {
      return;
    }

    if (
      props.isAuthenticated !== prevProps.isAuthenticated ||
      props.isBlocked !== prevProps.isBlocked ||
      props.pageSize !== prevProps.pageSize ||
      (prevProps.excludeContent && !props.excludeContent)
    ) {
      fetchPosts(tagId);
    }
  });

  useEffect(() => () => latest.current.setIsLoaded('tag', latest.current.tagId, false), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMore = useCallback((page: number) => fetchPosts(latest.current.tagId, page), []);
  const loadRest = useCallback(
    () => fetchPosts(latest.current.tagId, latest.current.page),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Stable identity: Pagination's memoized createPageLink remounts every link when it changes
  const createTagPageUrl = useCallback((page: number) => createPageUrl(page, tagUrl), [tagUrl]);

  const renderPosts = () => {
    if (isLayoutProGallery(layoutType)) {
      return (
        <PostListProGallery
          pageStart={page + 1}
          entityCount={entityCount}
          loadMore={showPagination ? undefined : loadMore}
          allPosts={showPagination ? currentPagePosts : allPosts}
          currentPagePosts={currentPagePosts}
          isLoading={isTagLoading && !isTagLoaded}
          canSeeMoreButton={canSeeMoreButton}
          section={SECTION_CATEGORY}
        />
      );
    }

    return (
      <PostList
        pageStart={page + 1}
        layoutType={layoutType}
        entityCount={entityCount}
        page={page}
        pageSize={pageSize}
        loadRest={showPagination ? loadRest : undefined}
        loadMore={showPagination ? undefined : loadMore}
        allPosts={showPagination ? currentPagePosts : allPosts}
        currentPagePosts={currentPagePosts}
        isLoading={isTagLoading && !isTagLoaded}
      />
    );
  };

  const isLoading = isTagLoading && !isTagLoaded;

  return (
    <PostTitleTagProvider titleFontParam={stylesParams.cssTitleFontCategory} fallbackTagName="h2">
      <Page noSpacing>
        <Layout>
          <AnimatedLoader isLoading={isLoading}>{renderPosts()}</AnimatedLoader>
          {!isLoading && showPagination ? (
            <Pagination
              dataHook="tags-pagination-container"
              page={page}
              pageSize={pageSize}
              entityCount={entityCount}
              createPageUrl={createTagPageUrl}
              handleNavigation={handleNavigation(tagPath, isSite)}
            />
          ) : null}
        </Layout>
      </Page>
    </PostTitleTagProvider>
  );
};

const connector = connect((state, ownProps: OwnProps, actions) => {
  const tagSlug = get(ownProps, ['params', 'tagSlug']);
  const tag = getTagBySlug(state, tagSlug);
  const tagId = tag?.id;

  const sectionUrl = getSectionUrl(state);
  const tagUrl = getTagUrl(sectionUrl, tagSlug);

  return {
    tagId,
    tagPath: 'tags',
    tagUrl,
    allPosts: tagId ? getSortedPostsByTagId(state, tagId) : [],
    currentPagePosts: tagId ? getPostsByTagIdAndPage(state, tagId, ownProps.page) : [],
    isTagLoading: getIsTagLoading(state, tagId),
    isTagLoaded: getIsTagLoaded(state, tagId),
    isSite: isSiteSelector(state),
    excludeContent: isExcludePostContentSupported(state),
    setIsLoaded: actions.setIsLoaded,
    fetchTagPosts: (params: { page: number; tagId: string }) => {
      return actions.fetchTagPostsWithAdapterPromisified({
        page: params.page,
        includeContent: isPostContentRequired(state),
        tagOptions: { id: params.tagId },
      });
    },
    canSeeMoreButton: getCanSeeMoreButton(state, ownProps.canSee),
  };
});

export default flowRight(
  withComponents,
  withPermissions,
  withPaginationSettings(SECTION_CATEGORY),
  withLayoutProps(),
  withAuth,
  withTranslate,
  connector,
)(TagsPage);
